import { useEffect, useState, useRef } from "react";
import {
  Typography,
  Menu,
  MenuItem,
  Box,
  Divider,
  MenuItemProps,
} from "@mui/material";
import {
  ReactNodeViewRenderer,
  NodeViewContent,
  NodeViewWrapper,
} from "@tiptap/react";
import TiptapTable, { TableOptions } from "@tiptap/extension-table";
import { addColumn, addRow } from "@tiptap/pm/tables";
import { Plus, Trash2, ClipboardCopy } from "lucide-react";
import { selectedRect, TableMap } from "prosemirror-tables";
import { DOMSerializer } from "prosemirror-model";

const CustomMenuItem = ({
  children,
  ...props
}: {
  children: React.ReactNode;
} & MenuItemProps) => {
  return (
    <MenuItem sx={{ padding: "0px" }} {...props}>
      {children}
    </MenuItem>
  );
};

export const TableWrapper = TiptapTable.extend<TableOptions>({
  addNodeView() {
    return ReactNodeViewRenderer(({ node, getPos, editor }: any) => {
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
      const [tooltipPosition, setTooltipPosition] = useState<{
        x: number;
        y: number;
      } | null>(null);

      const [isActive, setIsActive] = useState(false);
      const [isHovered, setIsHovered] = useState(false);
      const [resizingColumn, setResizingColumn] = useState<number | null>(null);
      const resizeStartX = useRef<number>(0);
      const resizeStartWidth = useRef<number>(0);
      const wrapperRef = useRef<HTMLDivElement>(null);

      useEffect(() => {
        if (!editor) return;

        const isSelected = () => {
          const { selection } = editor.state;
          const currentPos = getPos();
          return (
            currentPos <= selection.from &&
            selection.to <= currentPos + node.nodeSize
          );
        };

        const onSelectionUpdate = () => {
          setIsActive(isSelected());
        };

        setIsActive(isSelected());

        // This event fires on all cursor movements and selection changes
        editor.on("selectionUpdate", onSelectionUpdate);

        return () => {
          editor.off("selectionUpdate", onSelectionUpdate);
        };
      }, [editor, node, getPos]);

      const addColumnToEnd = () => {
        const { state, view } = editor;
        const pos = getPos();
        const { selection } = state;

        // If selection is not in this table, focus the first cell temporarily
        const isInTable =
          pos <= selection.from && selection.to <= pos + node.nodeSize;
        if (!isInTable) {
          editor.commands.focus();
          editor.commands.setTextSelection(pos + 2);
        }

        const table = selectedRect(editor.state);

        if (table) {
          const numberOfColumns = table.map.width;
          const { tr } = editor.state;
          const newTr = addColumn(tr, table, numberOfColumns);

          if (newTr) {
            view.dispatch(newTr);
          }
        }
      };
      const addRowToBottom = () => {
        const { state, view } = editor;
        const pos = getPos();
        const { selection } = state;

        const isInTable =
          pos <= selection.from && selection.to <= pos + node.nodeSize;
        if (!isInTable) {
          editor.commands.focus();
          editor.commands.setTextSelection(pos + 2);
        }

        const table = selectedRect(editor.state);

        if (table) {
          const numberOfRows = table.map.height;
          const { tr } = editor.state;
          const newTr = addRow(tr, table, numberOfRows);

          if (newTr) {
            view.dispatch(newTr);
          }
        }
      };
      const handleRightClick = (e: React.MouseEvent<HTMLTableCellElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const cell = e.target as HTMLElement;
        const rect = cell.getBoundingClientRect();
        setTooltipPosition({ x: rect.left, y: rect.top - 40 });
        setAnchorEl(cell);
        editor.chain().focus().run();
      };

      const runEditorAction = (action: () => void) => {
        action();
        setAnchorEl(null);
      };

      const handleResizeStart = (columnIndex: number, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(columnIndex);
        resizeStartX.current = e.clientX;

        const table = node;
        const map = TableMap.get(table);
        const cellPos = map.map[columnIndex];
        const cell = table.nodeAt(cellPos);
        resizeStartWidth.current = cell?.attrs.colwidth?.[0] || 100;
      };

      const handleResizeMove = (e: MouseEvent) => {
        if (resizingColumn === null) return;

        const delta = e.clientX - resizeStartX.current;
        const newWidth = Math.max(50, resizeStartWidth.current + delta);

        const pos = getPos();
        if (pos === undefined) return;

        const table = node;
        const map = TableMap.get(table);
        const { tr } = editor.state;

        for (let row = 0; row < map.height; row++) {
          const cellIndex = row * map.width + resizingColumn;
          const cellPos = map.map[cellIndex];
          const cell = table.nodeAt(cellPos);

          if (cell) {
            tr.setNodeMarkup(pos + cellPos + 1, null, {
              ...cell.attrs,
              colwidth: [newWidth],
            });
          }
        }

        editor.view.dispatch(tr);
      };

      const handleResizeEnd = () => {
        setResizingColumn(null);
      };

      useEffect(() => {
        if (resizingColumn !== null) {
          document.addEventListener("mousemove", handleResizeMove);
          document.addEventListener("mouseup", handleResizeEnd);
          return () => {
            document.removeEventListener("mousemove", handleResizeMove);
            document.removeEventListener("mouseup", handleResizeEnd);
          };
        }
      }, [resizingColumn]);

      const getColumnPositions = () => {
        if (!wrapperRef.current) return [];
        const table = wrapperRef.current.querySelector("table");
        if (!table) return [];
        const firstRow = table.querySelector("tr");
        if (!firstRow) return [];

        const cells = Array.from(firstRow.children) as HTMLElement[];
        const positions: number[] = [];
        let cumulativeLeft = 0;

        cells.forEach((cell) => {
          cumulativeLeft += cell.offsetWidth;
          positions.push(cumulativeLeft);
        });

        return positions;
      };

      const [columnPositions, setColumnPositions] = useState<number[]>([]);

      const showButtons = isActive || isHovered;

      useEffect(() => {
        const updatePositions = () => {
          const positions = getColumnPositions();
          setColumnPositions(positions);
        };

        updatePositions();

        const observer = new MutationObserver(updatePositions);
        if (wrapperRef.current) {
          observer.observe(wrapperRef.current, {
            attributes: true,
            subtree: true,
            childList: true,
          });
        }

        return () => {
          observer.disconnect();
        };
      }, [node]);

      return (
        <NodeViewWrapper
          className="custom-table-wrapper"
          onContextMenu={handleRightClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Box sx={{ display: "inline-flex", flexDirection: "column" }}>
            <Box
              ref={wrapperRef}
              sx={{ display: "flex", width: "100%", position: "relative" }}
            >
              <NodeViewContent
                as="table"
                className="custom-table-content"
                style={{
                  width: "auto",
                }}
              ></NodeViewContent>
              {showButtons &&
                columnPositions.map((left, i) => (
                  <Box
                    key={i}
                    onMouseDown={(e) => handleResizeStart(i, e)}
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      width: "4px",
                      cursor: "col-resize",
                      backgroundColor:
                        resizingColumn === i ? "#3B82F6" : "transparent",
                      "&:hover": {
                        backgroundColor: "#93C5FD",
                      },
                      zIndex: 10,
                      left: `${left - 2}px`,
                    }}
                  />
                ))}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
                // Use mouseDown instead of click to prevent the editor from losing focus.
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  addColumnToEnd();
                }}
              >
                {
                  <Plus
                    style={{
                      cursor: showButtons ? "pointer" : undefined,
                      visibility: !showButtons ? "hidden" : undefined,
                    }}
                    size={16}
                  />
                }
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                cursor: "pointer",
                marginRight: "16px", // to offset the right icon
              }}
              // Use mouseDown instead of click to prevent the editor from losing focus.
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                addRowToBottom();
              }}
            >
              <Plus
                style={{
                  cursor: showButtons ? "pointer" : undefined,
                  visibility: !showButtons ? "hidden" : undefined,
                }}
                size={16}
              />
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorPosition={
              tooltipPosition
                ? { top: tooltipPosition.y, left: tooltipPosition.x }
                : undefined
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: "12px 20px",
                gap: "20px",
              }}
            >
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                <Typography variant="bodySmallHeavy" color="#6B6E7B">
                  Column
                </Typography>
                <CustomMenuItem
                  onClick={() =>
                    runEditorAction(() =>
                      editor.chain().focus().addColumnBefore().run(),
                    )
                  }
                >
                  <Plus
                    size={20}
                    style={{ paddingLeft: "0px", marginRight: "8px" }}
                  ></Plus>
                  <Typography variant="body" color="#3D3D3D">
                    Add column left
                  </Typography>
                </CustomMenuItem>
                <CustomMenuItem
                  onClick={() =>
                    runEditorAction(() =>
                      editor.chain().focus().addColumnAfter().run(),
                    )
                  }
                >
                  <Plus
                    size={20}
                    style={{ paddingLeft: "0px", marginRight: "8px" }}
                  ></Plus>
                  <Typography variant="body" color="#3D3D3D">
                    Add column right
                  </Typography>
                </CustomMenuItem>
                <CustomMenuItem
                  onClick={() =>
                    runEditorAction(() =>
                      editor.chain().focus().deleteColumn().run(),
                    )
                  }
                >
                  <Trash2
                    size={20}
                    style={{ paddingLeft: "0px", marginRight: "8px" }}
                  ></Trash2>
                  <Typography variant="body" color="#3D3D3D">
                    Delete column
                  </Typography>
                </CustomMenuItem>
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                <Typography variant="bodySmallHeavy" color="#6B6E7B">
                  Row
                </Typography>
                <CustomMenuItem
                  onClick={() =>
                    runEditorAction(() =>
                      editor.chain().focus().addRowBefore().run(),
                    )
                  }
                >
                  <Plus
                    size={20}
                    style={{ paddingLeft: "0px", marginRight: "8px" }}
                  ></Plus>
                  <Typography variant="body" color="#3D3D3D">
                    Add row above
                  </Typography>
                </CustomMenuItem>
                <CustomMenuItem
                  onClick={() =>
                    runEditorAction(() =>
                      editor.chain().focus().addRowAfter().run(),
                    )
                  }
                >
                  <Plus
                    size={20}
                    style={{ paddingLeft: "0px", marginRight: "8px" }}
                  ></Plus>
                  <Typography variant="body" color="#3D3D3D">
                    Add row below
                  </Typography>
                </CustomMenuItem>
                <CustomMenuItem
                  onClick={() =>
                    runEditorAction(() =>
                      editor.chain().focus().deleteRow().run(),
                    )
                  }
                >
                  <Trash2
                    size={20}
                    style={{ paddingLeft: "0px", marginRight: "8px" }}
                  ></Trash2>
                  <Typography variant="body" color="#3D3D3D">
                    Delete row
                  </Typography>
                </CustomMenuItem>
              </Box>
              <Divider sx={{ marginLeft: "-20px", marginRight: "-20px" }} />
              <CustomMenuItem
                onClick={() => {
                  const { state } = editor;
                  const { selection, schema } = state;

                  const slice = selection.content();
                  const div = document.createElement("div");

                  const table = document.createElement("table");
                  const fragment = DOMSerializer.fromSchema(
                    schema,
                  ).serializeFragment(slice.content);
                  table.appendChild(fragment);
                  div.appendChild(table);

                  const html = div.innerHTML;
                  const text = Array.from(table.rows)
                    .map((row) =>
                      Array.from(row.cells)
                        .map((cell) => cell.innerText.trim())
                        .join("\t"),
                    )
                    .join("\n");

                  navigator.clipboard.write([
                    new ClipboardItem({
                      "text/html": new Blob([html], { type: "text/html" }),
                      "text/plain": new Blob([text], { type: "text/plain" }),
                    }),
                  ]);
                  setAnchorEl(null);
                }}
              >
                <ClipboardCopy
                  size={20}
                  style={{ paddingLeft: "0px", marginRight: "8px" }}
                ></ClipboardCopy>
                <Typography variant="body" color="#3D3D3D">
                  Copy to clipboard
                </Typography>
              </CustomMenuItem>
              <CustomMenuItem
                onClick={() => {
                  runEditorAction(() =>
                    editor.chain().focus().deleteTable().run(),
                  );
                }}
              >
                <Trash2
                  size={20}
                  style={{ paddingLeft: "0px", marginRight: "8px" }}
                  stroke="#C14743"
                ></Trash2>
                <Typography variant="body" color="secondary.dark">
                  Delete table
                </Typography>
              </CustomMenuItem>
            </Box>
          </Menu>
        </NodeViewWrapper>
      );
    });
  },
});
